import type { ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../../components/auth/index.js';
import { useAuth } from '../../hooks/index.js';
import { useEngineerEvaluationDetail } from '../../hooks/useEngineerEvaluation.js';
import { useEngineerEvaluations } from '../../hooks/useEngineerEvaluation.js';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';

/** @graph-connects none */
interface DetailSearch {
  quarter?: string;
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニアスキル評価詳細ページ。criteria別のevidenceResult・score・サマリー・リファレンスを表示
 * @graph-connects useEngineerEvaluationDetail [calls] エンジニア評価詳細フック呼び出し
 * @graph-connects useEngineerEvaluations [calls] 四半期一覧取得のためエンジニア評価一覧フック呼び出し
 */
export const Route = createFileRoute('/engineer-evaluations/$email')({
  component: EngineerEvaluationDetailGuard,
  validateSearch: (search: Record<string, unknown>): DetailSearch => ({
    quarter: typeof search['quarter'] === 'string' ? search['quarter'] : undefined,
  }),
});

/**
 * ログインユーザー自身の評価か（メールは大文字小文字を区別しない）
 * @graph-connects none
 */
export function isOwnEvaluation(userEmail: string | undefined, paramEmail: string): boolean {
  return (
    userEmail != null && userEmail.toLowerCase() === decodeURIComponent(paramEmail).toLowerCase()
  );
}

/**
 * 本人の評価は権限なしで開ける。他人の評価は VIEW_ENGINEER_EVALUATION を要求する
 * （詳細 API 側も本人・ロール別に絞っている）
 * @graph-connects useAuth [calls] ログインユーザーのメール取得
 * @graph-connects ProtectedRoute [calls] 他人の評価を開く場合の権限チェック
 */
export function EngineerEvaluationDetailGuard() {
  const { email } = Route.useParams();
  const { user } = useAuth();
  if (isOwnEvaluation(user?.email, email)) return <EngineerEvaluationDetailPage />;
  return (
    <ProtectedRoute pageKey="engineerEvaluation">
      <EngineerEvaluationDetailPage />
    </ProtectedRoute>
  );
}

/**
 * evidenceResult に応じたバッジを返す
 * @graph-connects none
 */
function getResultBadge(result: string, score: number | null) {
  switch (result) {
    case 'positive':
      return <Badge variant="success">Positive {score != null ? score.toFixed(1) : ''}</Badge>;
    case 'negative':
      return <Badge variant="error">Negative {score != null ? score.toFixed(1) : ''}</Badge>;
    default:
      return <Badge variant="default">No Evidence</Badge>;
  }
}

/**
 * evidenceSummary を構造化表示する
 * 【評価】【強み】【10点に向けて】セクションをパースし、旧形式（プレーンテキスト）にもフォールバック
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business evidenceSummary を【評価】【強み】【10点に向けて】セクションにパースし構造化表示。旧形式プレーンテキストにもフォールバック
 * @graph-connects none
 */
export function renderSummary(summary: string) {
  const sections = summary.split(/【(.+?)】/).filter(Boolean);

  // 旧形式: セクションヘッダーなし → そのまま段落表示
  if (sections.length <= 1) {
    return summary
      .split('\n')
      .filter(Boolean)
      .map((line, i) => {
        if (line.startsWith('- ')) {
          return (
            <li key={String(i)} className="text-[var(--color-text-primary)] ml-4 list-disc">
              {line.slice(2)}
            </li>
          );
        }
        return (
          <p key={String(i)} className="text-[var(--color-text-primary)]">
            {line}
          </p>
        );
      });
  }

  // 新形式: セクションごとにレンダリング
  const result: ReactNode[] = [];
  for (let i = 0; i < sections.length; i += 2) {
    const title = sections[i]?.trim();
    const content = sections[i + 1]?.trim();
    if (!title || !content) {
      continue;
    }

    const lines = content.split('\n').filter(Boolean);
    const isImprovement = title === '10点に向けて';

    result.push(
      <div key={title} className="space-y-1">
        <p
          className={`text-xs font-semibold ${isImprovement ? 'text-[var(--color-info)]' : 'text-[var(--color-text-muted)]'}`}
        >
          {title}
        </p>
        {lines.map((line, j) => {
          if (line.startsWith('- ')) {
            return (
              <li
                key={`${title}-${String(j)}`}
                className={`ml-4 list-disc text-sm ${isImprovement ? 'text-[var(--color-info)]' : 'text-[var(--color-text-primary)]'}`}
              >
                {line.slice(2)}
              </li>
            );
          }
          return (
            <p key={`${title}-${String(j)}`} className="text-sm text-[var(--color-text-primary)]">
              {line}
            </p>
          );
        })}
      </div>,
    );
  }
  return result;
}

/** @graph-connects none */
function EngineerEvaluationDetailPage() {
  const { email } = Route.useParams();
  const navigate = useNavigate();
  const decodedEmail = decodeURIComponent(email);

  // 四半期一覧を取得して最新を使う
  const { data: listData } = useEngineerEvaluations();
  const quarters = listData?.quarters ?? [];
  const { quarter: searchQuarter } = Route.useSearch();
  const selectedQuarter = searchQuarter ?? quarters[0] ?? '';

  const { data, isLoading, isError, refetch } = useEngineerEvaluationDetail(
    selectedQuarter ? { email: decodedEmail, quarter: selectedQuarter } : null,
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/engineer-evaluations"
          className="text-sm text-[var(--color-accent-primary)] hover:underline"
        >
          ← 一覧に戻る
        </Link>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/engineer-evaluations"
          className="text-sm text-[var(--color-accent-primary)] hover:underline"
        >
          ← 一覧に戻る
        </Link>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  const byCategory = new Map<string, typeof data.evaluations>();
  for (const ev of data.evaluations) {
    const cat = ev.category || 'その他';
    const list = byCategory.get(cat) ?? [];
    list.push(ev);
    byCategory.set(cat, list);
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/engineer-evaluations"
            className="text-sm text-[var(--color-accent-primary)] hover:underline"
          >
            ← 一覧に戻る
          </Link>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mt-2">
            {data.nickname}
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {data.email} · {data.level} · {data.teamName}
          </p>
        </div>
        {quarters.length > 0 && (
          <Select
            options={quarters.map((q) => ({ value: q, label: q }))}
            value={selectedQuarter}
            onChange={(e) => {
              void navigate({
                to: '/engineer-evaluations/$email',
                params: { email },
                search: { quarter: e.target.value },
              });
            }}
            aria-label="四半期選択"
          />
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">平均スコア</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">
            {data.averageScore != null ? data.averageScore.toFixed(1) : '-'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">Positive</p>
          <p className="text-2xl font-bold text-[var(--color-success)] mt-1">
            {String(data.positiveCount)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">Negative</p>
          <p className="text-2xl font-bold text-[var(--color-error)] mt-1">
            {String(data.negativeCount)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">四半期</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">{data.quarter}</p>
        </Card>
      </div>

      {/* Criteria by category */}
      {[...byCategory.entries()].map(([category, evals]) => {
        const catPositive = evals.filter((e) => e.evidenceResult === 'positive').length;
        const catNegative = evals.filter((e) => e.evidenceResult === 'negative').length;
        return (
          <Card key={category}>
            <CardHeader
              title={category}
              description={`+${String(catPositive)} -${String(catNegative)} / ${String(evals.length)} criteria`}
            />
            <CardContent>
              <div className="space-y-4">
                {evals.map((ev) => (
                  <div
                    key={ev.skillMapId}
                    className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">
                          {ev.skillName}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">{ev.skillMapId}</p>
                      </div>
                      {getResultBadge(ev.evidenceResult, ev.score)}
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] mb-2">
                      {ev.criteriaText}
                    </p>
                    {ev.evidenceSummary && (
                      <div className="text-sm bg-[var(--color-bg-surface)] p-3 rounded space-y-2">
                        {renderSummary(ev.evidenceSummary)}
                      </div>
                    )}
                    {ev.references.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {ev.references.map((ref, idx) => (
                          <p
                            key={`${ref.key}-${String(idx)}`}
                            className="text-xs text-[var(--color-text-muted)]"
                          >
                            [{ref.type}] {ref.key}: {ref.detail}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
